import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { AuthService, GlobalMessageType } from '@spartacus/core';
import { EMPTY, Observable, UnaryFunction, pipe } from 'rxjs';
import { catchError, filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { UserIdService } from '../../../core/auth';
import { GlobalMessageService } from '../../../core/global-message';
import { AreaOfUse, AreaOfUseEditParams, DataImport } from '../../../core/model';
import { AreaOfUseAdminConnector } from '../connectors/area-of-use-admin-connector';
import { AreaOfUseAdminActions } from '../store/actions';
import { StateWithAreaOfUseAdmin } from '../store/area-of-use-admin-state';
import { AreaOfUseAdminSelectors } from '../store/selectors';

@Injectable({ providedIn: 'root' })
export class AreaOfUseAdminService {
  constructor(
    private store: Store<StateWithAreaOfUseAdmin>,
    private userIdService: UserIdService,
    private authService: AuthService,
    private areaOfUseAdminConnector: AreaOfUseAdminConnector,
    private globalMessageService: GlobalMessageService
  ) {}

  private loadIfNotPresentPipe<T>(): UnaryFunction<Observable<T>, Observable<T>> {
    return pipe(
      withLatestFrom(this.getAreaOfUseListLoading(), this.authService.isUserLoggedIn()),
      tap(([areaOfUseCategories, loading, isUserLoggedIn]) => {
        if (!areaOfUseCategories && !loading && isUserLoggedIn) {
          this.loadAreaOfUseCategories();
        }
      }),
      filter(([areaOfUseCategories, _]) => !!areaOfUseCategories),
      map(([areaOfUseCategories, _]) => areaOfUseCategories)
    );
  }

  getAreaOfUseListLoading(): Observable<boolean> {
    return this.store.select(AreaOfUseAdminSelectors.getAreaOfUseAdminListLoading);
  }

  loadAreaOfUseListAdminReset() {
    this.store.dispatch(new AreaOfUseAdminActions.LoadAreaOfUseListAdminReset());
  }

  getAreaOfUseCategories(): Observable<AreaOfUse[]> {
    return this.store.pipe(select(AreaOfUseAdminSelectors.getAreaOfUseAdminCategories), this.loadIfNotPresentPipe());
  }

  getAreaOfUseCategoryByCode(code: string): Observable<AreaOfUse> {
    return this.store.pipe(select(AreaOfUseAdminSelectors.getAreaOfUseCategoryByCode(code)), this.loadIfNotPresentPipe());
  }
  // For provided Area of Use category codes, returns array of codes of categories that are currently in loading state.
  getAreaOfUseCategoriesLoadingCodesArray(codes: string[]): Observable<string[]> {
    return this.store.pipe(select(AreaOfUseAdminSelectors.getAreaOfUseCategoriesLoadingCodesArray(codes)));
  }

  getAreaOfUseCategoriesByCodes(codes: Array<string>): Observable<Array<AreaOfUse>> {
    return this.store.pipe(
      select(AreaOfUseAdminSelectors.getAreaOfUseAdminCategoriesByCodes(codes)),
      this.loadIfNotPresentPipe()
    );
  }

  loadAreaOfUseCategories(): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) => this.store.dispatch(new AreaOfUseAdminActions.LoadAreaOfUseListAdmin(userId)),
      () => {}
    );
  }

  createAreaOfUseCategory(name: string, parentAreaOfUseCode?: string): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) => {
        return this.store.dispatch(
          new AreaOfUseAdminActions.CreateAreaOfUseCategoryAdmin({
            userId,
            areaOfUseName: name,
            parentAreaOfUseCode: parentAreaOfUseCode,
          })
        );
      },
      () => {}
    );
  }

  deleteAreaOfUseCategory(code: string): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) => this.store.dispatch(new AreaOfUseAdminActions.DeleteAreaOfUseCategoryAdmin({ userId, code })),
      () => {}
    );
  }

  loadAreaOfUseCategoryDetails(code: string) {
    this.userIdService.takeUserId(true).subscribe(
      (userId) => this.store.dispatch(new AreaOfUseAdminActions.LoadAreaOfUseCategoryDetails({ userId, code })),
      () => {}
    );
  }

  getLoadAreaOfUseCategoryDetailsLoading() {
    return this.store.pipe(select(AreaOfUseAdminSelectors.getLoadAreaOfUseCategoryDetailsLoading));
  }

  loadAreaOfUseCategoryDetailsReset() {
    this.store.dispatch(new AreaOfUseAdminActions.LoadAreaOfUseCategoryDetailsReset());
  }

  editAreaOfUseCategory(code: string, params: AreaOfUseEditParams, requiresListReload = false): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) =>
        this.store.dispatch(new AreaOfUseAdminActions.EditAreaOfUseCategoryAdmin({ userId, code, params, requiresListReload })),
      () => {}
    );
  }

  publishAreasOfUse(): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) => this.store.dispatch(new AreaOfUseAdminActions.PublishAreasOfUseAdmin(userId)),
      () => {}
    );
  }

  getAreaOfUsePublishLoading(): Observable<boolean> {
    return this.store.select(AreaOfUseAdminSelectors.getAreaOfUseAdminPublishLoading);
  }

  loadAreasOfUseForProduct(productCode: string): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) => this.store.dispatch(new AreaOfUseAdminActions.LoadAreasOfUseForProduct({ userId, productCode })),
      () => {}
    );
  }

  loadAreasOfUseForProductReset(): void {
    this.store.dispatch(new AreaOfUseAdminActions.LoadAreasOfUseForProductReset());
  }

  getLoadAreasOfUseForProductLoading(): Observable<boolean> {
    return this.store.pipe(select(AreaOfUseAdminSelectors.getLoadAreasOfUseForProductLoading));
  }

  addProductToAreaOfUse(productCode: string, categoryCode: string) {
    this.userIdService.takeUserId(true).subscribe(
      (userId) => this.store.dispatch(new AreaOfUseAdminActions.AddProductToAreaOfUse({ userId, productCode, categoryCode })),
      () => {}
    );
  }

  excludeArticlesFromAreaOfUse(categoryCode: string, excludedArticleCodes: string[], changedArticleCodes: string[]): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) =>
        this.store.dispatch(
          new AreaOfUseAdminActions.ExcludeArticlesFromAreaOfUse({
            userId,
            categoryCode,
            excludedArticleCodes,
            changedArticleCodes,
          })
        ),
      () => {}
    );
  }

  getAddProductToAreaOfUseLoading(): Observable<boolean> {
    return this.store.pipe(select(AreaOfUseAdminSelectors.getAddProductToAreaOfUseLoading));
  }

  getAddProductToAreaOfUseSuccess(): Observable<boolean> {
    return this.store.pipe(
      select(AreaOfUseAdminSelectors.getAddProductToAreaOfUseSuccess),
      filter((success) => success)
    );
  }

  addProductToAreaOfUseReset(): void {
    this.store.dispatch(new AreaOfUseAdminActions.AddProductToAreaOfUseReset());
  }

  removeProductsFromAreaOfUse(productCodes: string[], categoryCode: string): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) =>
        this.store.dispatch(new AreaOfUseAdminActions.RemoveProductsFromAreaOfUse({ userId, productCodes, categoryCode })),
      () => {}
    );
  }

  getRemoveProductsFromAreaOfUseLoading(): Observable<boolean> {
    return this.store.pipe(select(AreaOfUseAdminSelectors.getRemoveProductsFromAreaOfUseLoading));
  }

  getRemoveProductsFromAreaOfUseSuccess(): Observable<boolean> {
    return this.store.pipe(
      select(AreaOfUseAdminSelectors.getRemoveProductsFromAreaOfUseSuccess),
      filter((success) => success)
    );
  }

  removeProductsFromAreaOfUseReset(): void {
    this.store.dispatch(new AreaOfUseAdminActions.RemoveProductsFromAreaOfUseReset());
  }

  replaceAreaOfUseProduct(code: string, oldProductCode: string, newProductCode: string): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) =>
        this.store.dispatch(new AreaOfUseAdminActions.ReplaceAreaOfUseAdminProduct(code, userId, oldProductCode, newProductCode)),
      () => {}
    );
  }

  getReplaceAreaOfUseProductLoading(): Observable<boolean> {
    return this.store.pipe(select(AreaOfUseAdminSelectors.getReplaceAreaOfUseProductLoading));
  }

  // Export and Import
  getExportedAreaOfUseFile(areaOfUseCode: string): Observable<Blob> {
    return this.userIdService.takeUserId(true).pipe(
      switchMap((userId) => this.areaOfUseAdminConnector.getExportedAreaOfUseFile(userId, areaOfUseCode)),
      catchError(() => {
        this.handleExportError();
        return EMPTY;
      })
    );
  }

  protected handleExportError(): void {
    this.globalMessageService.add({ key: 'areaOfUse.exportFailure_message' }, GlobalMessageType.MSG_TYPE_ERROR);
  }

  sendImportAreaOfUseFile(areaOfUseCode: string, file: File): Observable<DataImport> {
    return this.userIdService.takeUserId(true).pipe(
      switchMap((userId) => this.areaOfUseAdminConnector.sendImportAreaOfUseFile(userId, areaOfUseCode, file)),
      catchError((err) => {
        this.handleImportError();
        throw err;
      })
    );
  }

  protected handleImportError(): void {
    this.globalMessageService.add({ key: 'areaOfUse.importFailure_message' }, GlobalMessageType.MSG_TYPE_ERROR);
  }

  downloadImportAreaOfUseFileTemplate(areaOfUseCode: string): Observable<Blob> {
    return this.userIdService.takeUserId(true).pipe(
      switchMap((userId) => this.areaOfUseAdminConnector.downloadImportAreaOfUseFileTemplate(userId, areaOfUseCode)),
      catchError(() => {
        this.handleExportFileTemplateError();
        return EMPTY;
      })
    );
  }
  protected handleExportFileTemplateError(): void {
    this.globalMessageService.add(
      { key: 'areaOfUse.importFileTemplateDownloadFailure_message' },
      GlobalMessageType.MSG_TYPE_ERROR
    );
  }
}
